import type { CameraSpec } from '../services/cameraLayoutService';
import {
  wallCameraToPoolGapM,
  wallOpticalCenterMeters,
  wallSideFromPlaceLabel,
} from './cameraGridPlacement';

/** 실제 CCTV에서 조절 가능한 항목만: 방위(팬)·하향(틸트)·초점(줌) */
export type CanvasHandleHit = 'heading' | 'tilt' | 'focal' | 'camera';

export type FootprintHandleCanvas = {
  id: Exclude<CanvasHandleHit, 'camera'>;
  x: number;
  y: number;
  nx: number;
  ny: number;
  /** 드래그 축 단위벡터(캔버스 px 방향) — 병행 표시용 */
  axisX: number;
  axisY: number;
};

function headingUnitRad(headingDeg: number) {
  const h = (headingDeg * Math.PI) / 180;
  return { fx: Math.cos(h), fy: Math.sin(h), rx: -Math.sin(h), ry: Math.cos(h) };
}

/** 점들을 캔버스 inset 안으로만 클램프 */
function clampIntoCanvas(
  p: { x: number; y: number },
  canvasW: number,
  canvasH: number,
  margin: number,
): { x: number; y: number } {
  return {
    x: Math.min(canvasW - margin, Math.max(margin, p.x)),
    y: Math.min(canvasH - margin, Math.max(margin, p.y)),
  };
}

function separateHandles(
  points: { x: number; y: number }[],
  minDist: number,
  canvasW: number,
  canvasH: number,
  margin: number,
): { x: number; y: number }[] {
  const out = points.map((p) => ({ ...p }));
  for (let iter = 0; iter < 8; iter++) {
    for (let i = 0; i < out.length; i++) {
      for (let j = i + 1; j < out.length; j++) {
        const dx = out[j].x - out[i].x;
        const dy = out[j].y - out[i].y;
        const d = Math.hypot(dx, dy);
        if (d >= minDist || d < 1e-6) continue;
        const push = (minDist - d) / 2;
        const ux = dx / (d || 1);
        const uy = dy / (d || 1);
        out[i].x -= ux * push;
        out[i].y -= uy * push;
        out[j].x += ux * push;
        out[j].y += uy * push;
      }
    }
    for (let i = 0; i < out.length; i++) {
      out[i] = clampIntoCanvas(out[i], canvasW, canvasH, margin);
    }
  }
  return out;
}

/**
 * 화면 핸들 3개: 방위 · 하향 · 초점(줌).
 * (시야거리·독립 H/V는 실제 카메라에 없음 → 제외)
 */
export function footprintHandlesOnCanvas(
  cam: CameraSpec,
  _polygon: number[][],
  toCanvas: (nx: number, ny: number) => { x: number; y: number },
  canvasW: number,
  canvasH: number,
  margin = 18,
  poolLengthM = 25,
  poolWidthM = 12,
  bounds?: NormBounds,
): FootprintHandleCanvas[] | null {
  const marker = cameraMarkerNorm(cam, poolLengthM, poolWidthM, bounds);
  const camP = toCanvas(marker.x, marker.y);
  const { fx, fy } = headingUnitRad(Number(cam.heading_deg) || 0);

  const toCenter = { x: canvasW * 0.5 - camP.x, y: canvasH * 0.5 - camP.y };
  let ax = fx;
  let ay = fy;
  if (ax * toCenter.x + ay * toCenter.y < 0) {
    ax = -ax;
    ay = -ay;
  }
  if (Math.hypot(ax, ay) < 1e-6) {
    ax = toCenter.x;
    ay = toCenter.y;
  }
  const nA = Math.hypot(ax, ay) || 1;
  ax /= nA;
  ay /= nA;
  const lx = -ay;
  const ly = ax;

  const alongDist = Math.min(120, Math.max(56, Math.min(canvasW, canvasH) * 0.14));
  const latDist = Math.min(70, Math.max(36, alongDist * 0.55));

  let headingPt = {
    x: camP.x + ax * alongDist,
    y: camP.y + ay * alongDist,
  };
  let tiltPt = {
    x: camP.x + ax * alongDist * 0.62,
    y: camP.y + ay * alongDist * 0.62,
  };
  let focalPt = {
    x: camP.x + ax * alongDist + lx * latDist,
    y: camP.y + ay * alongDist + ly * latDist,
  };

  headingPt = clampIntoCanvas(headingPt, canvasW, canvasH, margin);
  tiltPt = clampIntoCanvas(tiltPt, canvasW, canvasH, margin);
  focalPt = clampIntoCanvas(focalPt, canvasW, canvasH, margin);

  const separated = separateHandles(
    [headingPt, tiltPt, focalPt],
    28,
    canvasW,
    canvasH,
    margin,
  );
  headingPt = separated[0];
  tiltPt = separated[1];
  focalPt = separated[2];

  return [
    {
      id: 'heading',
      x: headingPt.x,
      y: headingPt.y,
      nx: cam.x + fx * 12,
      ny: cam.y + fy * 12,
      axisX: lx,
      axisY: ly,
    },
    {
      id: 'tilt',
      x: tiltPt.x,
      y: tiltPt.y,
      nx: cam.x + fx * 9,
      ny: cam.y + fy * 9,
      axisX: ax,
      axisY: ay,
    },
    {
      id: 'focal',
      x: focalPt.x,
      y: focalPt.y,
      nx: cam.x + fx * 12 + lx * 0.1,
      ny: cam.y + fy * 12 + ly * 0.1,
      axisX: lx,
      axisY: ly,
    },
  ];
}

export function normHeadingDegFromPoint(
  cam: CameraSpec,
  nx: number,
  ny: number,
  poolLengthM = 25,
  poolWidthM = 12,
  bounds?: NormBounds,
): number {
  const m = cameraMarkerNorm(cam, poolLengthM, poolWidthM, bounds);
  const deg = (Math.atan2(ny - m.y, nx - m.x) * 180) / Math.PI;
  return Math.round(((((deg % 360) + 360) % 360) * 10) / 10);
}

/** 드래그 점 → 수평 화각(°) — 초점(줌) 환산용 */
export function horizontalFovDegFromPoint(
  cam: CameraSpec,
  nx: number,
  ny: number,
  poolLengthM = 25,
  poolWidthM = 12,
  bounds?: NormBounds,
): number {
  const m = cameraMarkerNorm(cam, poolLengthM, poolWidthM, bounds);
  const center = (Number(cam.heading_deg) * Math.PI) / 180;
  const ang = Math.atan2(ny - m.y, nx - m.x);
  let diff = ang - center;
  while (diff > Math.PI) diff -= 2 * Math.PI;
  while (diff < -Math.PI) diff += 2 * Math.PI;
  const half = Math.abs(diff);
  const deg = (half * 2 * 180) / Math.PI;
  return Math.max(22, Math.min(140, Math.round(deg * 10) / 10));
}

/** 바닥 목표점 → 하향각(°) */
export function tiltDegFromGroundPoint(
  cam: CameraSpec,
  nx: number,
  ny: number,
  poolLengthM: number,
  poolWidthM: number,
  bounds?: NormBounds,
): number {
  const h = Math.max(0.5, Number(cam.height_m) || 4.5);
  const m = cameraMarkerNorm(cam, poolLengthM, poolWidthM, bounds);
  const dxM = ((nx - m.x) / 100) * poolLengthM;
  const dyM = ((ny - m.y) / 100) * poolWidthM;
  const horiz = Math.hypot(dxM, dyM);
  const tilt = (Math.atan2(h, Math.max(0.25, horiz)) * 180) / Math.PI;
  return Math.max(15, Math.min(72, Math.round(tilt * 10) / 10));
}

export function hitFootprintHandleCanvas(
  cxPx: number,
  cyPx: number,
  handles: FootprintHandleCanvas[],
  camCanvas: { x: number; y: number },
  radiusPx = 14,
): CanvasHandleHit | null {
  for (const h of handles) {
    if (Math.hypot(h.x - cxPx, h.y - cyPx) <= radiusPx) return h.id;
  }
  if (Math.hypot(camCanvas.x - cxPx, camCanvas.y - cyPx) <= radiusPx) return 'camera';
  return null;
}

export function drawHandleDragAxis(
  ctx: CanvasRenderingContext2D,
  handle: FootprintHandleCanvas,
  color: string,
  halfLen = 28,
) {
  const n = Math.hypot(handle.axisX, handle.axisY) || 1;
  const ux = handle.axisX / n;
  const uy = handle.axisY / n;
  ctx.save();
  ctx.strokeStyle = color;
  ctx.globalAlpha = 0.55;
  ctx.lineWidth = 2;
  ctx.setLineDash([5, 4]);
  ctx.beginPath();
  ctx.moveTo(handle.x - ux * halfLen, handle.y - uy * halfLen);
  ctx.lineTo(handle.x + ux * halfLen, handle.y + uy * halfLen);
  ctx.stroke();
  ctx.restore();
}

type Vec3 = [number, number, number];

function norm3(x: number, y: number, z: number): Vec3 {
  const n = Math.hypot(x, y, z) || 1;
  return [x / n, y / n, z / n];
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

function cameraBasis(headingDeg: number, tiltDeg: number): {
  forward: Vec3;
  right: Vec3;
  up: Vec3;
} {
  const heading = (headingDeg * Math.PI) / 180;
  const tilt = (Math.max(0, Math.min(90, tiltDeg)) * Math.PI) / 180;
  const forward = norm3(
    Math.cos(tilt) * Math.cos(heading),
    Math.cos(tilt) * Math.sin(heading),
    -Math.sin(tilt),
  );
  let right: Vec3;
  if (Math.abs(forward[2]) > 0.95) {
    right = [-Math.sin(heading), Math.cos(heading), 0];
  } else {
    right = norm3(...cross(forward, [0, 0, 1]));
  }
  const up = norm3(...cross(right, forward));
  return { forward, right, up };
}

function effectiveCamXyM(
  cam: CameraSpec,
  poolLengthM: number,
  poolWidthM: number,
): { x: number; y: number } {
  const L = Math.max(poolLengthM, 1);
  const W = Math.max(poolWidthM, 1);
  const x = (Number(cam.x) / 100) * L;
  const y = (Number(cam.y) / 100) * W;
  if (cam.mount_type !== 'wall') return { x, y };
  return wallOpticalCenterMeters(
    x,
    y,
    poolLengthM,
    poolWidthM,
    Number(cam.pool_edge_offset_m) || 0,
    Number(cam.wall_mount_offset_m) || 0,
    wallSideFromPlaceLabel(String(cam.name || '')),
  );
}

export type NormBounds = { minX: number; maxX: number; minY: number; maxY: number };

/**
 * 도면 마커(정규화 %).
 * 벽캠: 풀 가장자리에서 (벽·풀 − 벽·카메라) m 만큼 바깥.
 * 예: 1.0 − 0.2 = 0.8m → 풀 폭 12m 기준 span의 0.8/12.
 */
export function cameraMarkerNorm(
  cam: CameraSpec,
  poolLengthM: number,
  poolWidthM: number,
  bounds?: NormBounds,
): { x: number; y: number } {
  const x = Number(cam.x) || 50;
  const y = Number(cam.y) || 50;
  if (cam.mount_type !== 'wall') return { x, y };

  const shiftM = wallCameraToPoolGapM(
    Number(cam.pool_edge_offset_m) || 0,
    Number(cam.wall_mount_offset_m) || 0,
  );
  if (shiftM <= 1e-9) return { x, y };

  const L = Math.max(poolLengthM, 1);
  const W = Math.max(poolWidthM, 1);
  const b = bounds ?? { minX: 0, maxX: 100, minY: 0, maxY: 100 };
  const spanX = Math.max(1e-6, b.maxX - b.minX);
  const spanY = Math.max(1e-6, b.maxY - b.minY);

  const dTop = Math.abs(y - b.minY);
  const dBottom = Math.abs(b.maxY - y);
  // 단변에 미세 가산 → 구석에서도 위·아래 장변 쪽으로 마커 배치
  const dLeft = Math.abs(x - b.minX) + 1e-3;
  const dRight = Math.abs(b.maxX - x) + 1e-3;
  const nearest = Math.min(dTop, dBottom, dLeft, dRight);

  // 풀 한 변(실측) 대비 이동량 → 그 변이 차지하는 정규 span 에만 비례
  if (nearest === dTop) {
    return { x, y: b.minY - (shiftM / W) * spanY };
  }
  if (nearest === dBottom) {
    return { x, y: b.maxY + (shiftM / W) * spanY };
  }
  if (nearest === dLeft) {
    return { x: b.minX - (shiftM / L) * spanX, y };
  }
  return { x: b.maxX + (shiftM / L) * spanX, y };
}

/** 도면 마커 좌표 → 저장용(풀 가장자리 위) 정규화 % */
export function cameraStoredNormFromMarker(
  nx: number,
  ny: number,
  cam: CameraSpec,
  poolLengthM: number,
  poolWidthM: number,
  bounds?: NormBounds,
): { x: number; y: number } {
  if (cam.mount_type !== 'wall') {
    return { x: Math.round(nx * 10) / 10, y: Math.round(ny * 10) / 10 };
  }
  const shiftM = wallCameraToPoolGapM(
    Number(cam.pool_edge_offset_m) || 0,
    Number(cam.wall_mount_offset_m) || 0,
  );
  if (shiftM <= 1e-9) {
    return { x: Math.round(nx * 10) / 10, y: Math.round(ny * 10) / 10 };
  }
  const b = bounds ?? { minX: 0, maxX: 100, minY: 0, maxY: 100 };
  const x0 = Number(cam.x) || 50;
  const y0 = Number(cam.y) || 50;
  const dTop = Math.abs(y0 - b.minY);
  const dBottom = Math.abs(b.maxY - y0);
  const dLeft = Math.abs(x0 - b.minX);
  const dRight = Math.abs(b.maxX - x0);
  const nearest = Math.min(dTop, dBottom, dLeft, dRight);

  let sx = nx;
  let sy = ny;
  if (nearest === dTop) {
    sx = nx;
    sy = b.minY;
  } else if (nearest === dBottom) {
    sx = nx;
    sy = b.maxY;
  } else if (nearest === dLeft) {
    sx = b.minX;
    sy = ny;
  } else {
    sx = b.maxX;
    sy = ny;
  }

  return { x: Math.round(sx * 10) / 10, y: Math.round(sy * 10) / 10 };
}

/**
 * 백엔드 footprint_polygon_norm 과 동일한 수면 투영 — 드래그 중 실시간 미리보기용.
 * 틸트·방위·초점(H/V)·높이가 바뀌면 바닥 시야가 바로 반영됩니다.
 */
export function previewFootprintPolygonNorm(
  cam: CameraSpec,
  poolLengthM: number,
  poolWidthM: number,
  rays = 20,
): number[][] {
  const L = Math.max(poolLengthM, 1);
  const W = Math.max(poolWidthM, 1);
  const { x: cx, y: cy } = effectiveCamXyM(cam, L, W);
  const h = Math.max(0.5, Number(cam.height_m) || 4.5);
  const heading = Number(cam.heading_deg) || 90;
  const tilt = Number(cam.tilt_deg) || 60;
  const fovH = Number(cam.fov_h_deg) || 80;
  const fovV = Number(cam.fov_v_deg) || fovH * 0.75;
  const maxRange = Math.max(5, Number(cam.max_range_m) || 35);
  const { forward, right, up } = cameraBasis(heading, tilt);

  const edge: [number, number][] = [];
  const n = Math.max(rays - 1, 1);
  for (let i = 0; i < rays; i++) {
    const t = i / n;
    edge.push([-fovH / 2 + t * fovH, -fovV / 2]);
  }
  for (let i = 0; i < rays; i++) {
    const t = i / n;
    edge.push([fovH / 2, -fovV / 2 + t * fovV]);
  }
  for (let i = 0; i < rays; i++) {
    const t = i / n;
    edge.push([fovH / 2 - t * fovH, fovV / 2]);
  }
  for (let i = 0; i < rays; i++) {
    const t = i / n;
    edge.push([-fovH / 2, fovV / 2 - t * fovV]);
  }

  const pts: number[][] = [];
  for (const [hAng, vAng] of edge) {
    const hz = (hAng * Math.PI) / 180;
    const vz = (vAng * Math.PI) / 180;
    let lx = Math.tan(hz);
    let ly = Math.tan(vz);
    let lz = 1;
    const ln = Math.hypot(lx, ly, lz) || 1;
    lx /= ln;
    ly /= ln;
    lz /= ln;
    const wx = forward[0] * lz + right[0] * lx + up[0] * ly;
    const wy = forward[1] * lz + right[1] * lx + up[1] * ly;
    const wz = forward[2] * lz + right[2] * lx + up[2] * ly;
    let gx: number;
    let gy: number;
    if (tilt >= 85 && wz >= -1e-6) {
      const horiz = Math.max(Math.cos((tilt * Math.PI) / 180), 0.05) * maxRange;
      gx = cx + Math.cos(((heading + hAng) * Math.PI) / 180) * horiz;
      gy = cy + Math.sin(((heading + hAng) * Math.PI) / 180) * horiz;
    } else {
      let t = wz < -1e-6 ? -h / wz : maxRange;
      if (t < 0 || t > maxRange) t = Math.min(Math.max(t, 0), maxRange);
      gx = cx + wx * t;
      gy = cy + wy * t;
    }
    pts.push([(gx / L) * 100, (gy / W) * 100]);
  }
  return pts;
}
